import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { fromAccounts, fromOrder, useAppDispatch } from '../../store';
import { useQuery } from '../../routes';
import { Page } from '../../components/page';
import OrderDetailSection from './component/OrderDetailSection';
import { ProductSelection } from './component/ProductSelection';
import PriceSummary from './component/PriceSummary';
import { Panel } from '../../components/common/Panel';

const useStyles = makeStyles(({
  newCustomerTextContainer: {
    marginTop: 2,
  },
  newCustomerText: {
    backgroundColor: '#E8E5FF',
    fontWeight: 600,
    fontSize: 14,
    paddingTop: 1,
    paddingRight: 5,
    paddingBottom: 2,
    paddingLeft: 5,
  },
  newCustomerDescription: {
    fontSize: 14,
    fontWeight: 700,
    color: '#414141',
    marginLeft: 5,
  },
}));

export const PlanLegacy = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const [pendingOrderHeight, setPendingOrderHeight] = useState(0);
  const [priceSummaryHeight, setPriceSummaryHeight] = useState(0);
  const isOptedInFirstThreeLicenses = useSelector(fromAccounts.selectIsOptedInFirstThreeLicenses);
  const [showOptInFirstThreeLicenses, setShowOptInFirstThreeLicenses] = useState(false);

  const pendingOrderRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setPendingOrderHeight(node.getBoundingClientRect().height);
    }
  }, []);

  const priceSummaryRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setPriceSummaryHeight(node.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    dispatch(fromOrder.doFetchAllOrder(accountId));
    dispatch(fromAccounts.doCheckFirstThreeLicenses());
  }, []);

  const fetchData = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromOrder.resetOrderPreviewRequest()),
        dispatch(fromOrder.resetOrderPreviewResponse()),
      ]);
    }
  };

  useEffect(() => {
    setShowOptInFirstThreeLicenses(isOptedInFirstThreeLicenses);
  }, [isOptedInFirstThreeLicenses]);

  return (
    <Page
      id="expand-plan"
      headerId="page.expand.plan.header"
      fetchData={fetchData}
    >
      <Panel
        id="expand-plan-panel"
        sx={theme => ({
          margin: theme.spacing(4, 0),
        })}
      >
        {/* pending order warning */}
        {pendingOrder && pendingOrder.length > 0
          && (
            <Box
              id="expand-plan-banner"
              display="flex"
              ref={pendingOrderRef}
            >
              <OrderDetailSection pendingOrder={pendingOrder[0]} />
            </Box>
          )}
        <Box
          id="expand-plan-box"
          key="expand-plan-box"
          display="flex"
          p={4}
          sx={theme => ({
            flexDirection: 'row',
            height: `calc(98% - ${pendingOrderHeight}px)`,
            minHeight: theme.spacing(70),
            [theme.breakpoints.down('tabletL')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Box
            id="product-listing"
            display="flex"
            flexDirection="column"
            padding={theme => theme.spacing(0)}
            sx={theme => ({
              flexDirection: 'column',
              [theme.breakpoints.up('tabletL')]: {
                width: '70%',
                height: '100%',
              },
              [theme.breakpoints.down('tabletL')]: {
                padding: theme.spacing(2, 0, 0, 0),
                width: '100%',
                minHeight: '70%',
                height: `calc(100% - ${priceSummaryHeight}px)`,
              },
            })}
          >
            <Box id="product-selection-header" paddingBottom={1}>
              <Typography
                variant="body1"
                fontWeight={600}
                fontSize={21}
              >
                <FormattedMessage id="page.expand.plan.title" />
              </Typography>
              {showOptInFirstThreeLicenses && (
                <div className={classes.newCustomerTextContainer}>
                  <span className={classes.newCustomerText}>
                    <FormattedMessage id="page.expand.plan.new.customers.title" />
                  </span>
                  <span className={classes.newCustomerDescription}>
                    <FormattedMessage id="page.expand.plan.new.customers.description" />
                  </span>
                </div>
              )}
            </Box>
            {/* Product selection */}
            <ProductSelection />
          </Box>
          <Divider
            sx={theme => ({
              width: '1px',
              backgroundColor: theme.common.grey[700],
              height: '100%',
              orientation: 'vertical',
              [theme.breakpoints.down('tabletL')]: {
                display: 'none',
              },
            })}
            flexItem
          />
          <Box
            id="price-summary"
            display="flex"
            flexDirection="column"
            ref={priceSummaryRef}
            sx={theme => ({
              [theme.breakpoints.up('tabletL')]: {
                width: '30%',
                height: '100%',
                minWidth: theme.spacing(40),
                padding: theme.spacing(0),
                paddingLeft: theme.spacing(4),
              },
              [theme.breakpoints.down('tabletL')]: {
                width: '100%',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                overflow: 'scroll',
              },
            })}
          >
            <PriceSummary />
          </Box>
        </Box>
      </Panel>
    </Page>
  );
};
